import { gapsMetrics, type Gaps, type Ranked } from '@ads/shared';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import * as Ic from '../components/icons';
import { dateTime } from '../lib/format';
import { useGapsReading } from '../lib/queries';

/* ─────────────────────────────────────────────────────────────────────
   Mapa de Gaps · Consolidado. Os números saem do mapa na hora — a cada
   célula editada, a cada evidência enviada. A maturidade real, a leitura
   e as recomendações são do agente, que relê o mapa alguns segundos
   depois de cada mudança.
   ───────────────────────────────────────────────────────────────────── */

const n1 = (x: number) => x.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const short = (name: string) => name.replace(/^Fatores?\s+/i, '').replace(/ de Inovação$/, '').replace(/ e Entrega$/, '');

export function GapsDashboard({ content }: { content: Gaps }) {
  const m = useMemo(() => gapsMetrics(content), [content]);
  const q = useGapsReading();
  const r = q.data;
  const reading = r?.reading ?? null;
  const updating = !!r && (r.pending || r.stale);

  return (
    <div className="gd">
      {/* ── 1 · a equipe e a cobertura ── */}
      <div className="gd-row gd-kpis">
        <Kpi icon={<Ic.Nodes />} title="Pessoas atribuídas" sub="Total de responsáveis pelos gaps"
          value={m.people.total} aside={m.people.total ? <>Média de<b>{n1(m.people.gapsPerPerson)} gaps por pessoa</b></> : 'Ninguém atribuído ainda'} />
        <Kpi icon={<Ic.Grid4 />} title="Setores envolvidos" sub="Setores com gaps atribuídos"
          value={m.sectors.total} aside={m.sectors.total ? <>Média de<b>{n1(m.sectors.perFactor)} por fator</b></> : 'Nenhum setor indicado'} />
        <RingKpi icon={<Ic.CheckSquare />} title="Gaps com responsável definido" sub="Indicadores com pelo menos um responsável"
          pct={m.owners.pct} detail={`${m.owners.with} de ${m.total} gaps`} />
        <RingKpi icon={<Ic.Doc />} title="Indicadores com evidência" sub="Indicadores com documentos anexados"
          pct={m.evidence.pct} detail={`${m.evidence.with} de ${m.total} · ${m.evidence.files} ${m.evidence.files === 1 ? 'arquivo' : 'arquivos'}`} />
      </div>

      {/* ── 2 · quem e onde ── */}
      <div className="gd-row gd-three">
        <Card icon={<Ic.Nodes />} title="Responsáveis mais apontados" sub="Pessoas mais atribuídas como responsáveis">
          <Ranking total={m.people.total} unit={m.people.total === 1 ? 'pessoa' : 'pessoas'} items={m.people.top} others={m.people.others} empty="Preencha a coluna Quem? para ver quem concentra os gaps." />
        </Card>
        <Card icon={<Ic.Grid4 />} title="Setores mais apontados" sub="Setores mais atribuídos nos gaps">
          <Ranking total={m.sectors.actions} unit={m.sectors.actions === 1 ? 'ação' : 'ações'} items={m.sectors.top} others={m.sectors.others} empty="Preencha a coluna Onde? para ver os setores envolvidos." />
        </Card>
        <Card icon={<Ic.Bars />} title="Pessoas por fator" sub="Pessoas distintas atribuídas em cada fator">
          <HBars items={m.factors.map(f => ({ label: short(f.name), value: f.people, display: String(f.people) }))} max={Math.max(1, ...m.factors.map(f => f.people))} />
        </Card>
      </div>

      {/* ── 3 · onde está o gap ── */}
      <div className="gd-row gd-three gd-mid">
        <Card icon={<Ic.Target />} title="Maiores gaps por indicador" sub="Distância entre a maturidade de hoje e a meta">
          {m.biggest.length
            ? <HBars items={m.biggest.map(b => ({ label: b.name, value: b.gap, display: `${b.maturity} → ${b.target}`, title: `${b.name} (${short(b.factor)}): maturidade ${b.maturity}, meta ${b.target}` }))} max={4} />
            : <p className="gd-empty">Avalie a maturidade dos indicadores para ver onde estão os maiores gaps.</p>}
        </Card>
        <Card icon={<Ic.Bars />} title="Maturidade declarada × real (IA)" sub="Declarada no mapa, e a que as evidências sustentam" wide>
          <Compare factors={m.factors.map(f => {
            const real = reading?.maturidadeReal.find(x => x.factor === f.key);
            return { key: f.key, label: short(f.name), declared: f.declaredPct, real: real?.value ?? null, why: real?.motivo ?? '' };
          })} updating={updating} />
        </Card>
        <Card icon={<Ic.Doc />} title="Cobertura de evidências por fator" sub="Indicadores com documento anexado">
          <HBars items={m.factors.map(f => ({ label: short(f.name), value: Math.round(f.coverage * 100), display: `${Math.round(f.coverage * 100)}%`, aside: `${f.withEvidence} de ${f.indicators}` }))} max={100} />
        </Card>
      </div>

      {/* ── 4 · a leitura do agente ── */}
      <div className="gd-row gd-two">
        <Card icon={<Ic.Sparkle />} title="Leitura da IA" sub="Os principais achados do consolidado"
          status={<ReadingStatus updating={updating} basic={r?.basic ?? false} at={r?.updatedAt ?? null} />}>
          {reading
            ? <p className={`gd-reading${updating ? ' is-stale' : ''}`}>{reading.leitura}</p>
            : <p className="gd-empty">{updating ? 'O ADS está lendo o mapa…' : 'A leitura aparece quando o mapa tiver os primeiros responsáveis, evidências e maturidades.'}</p>}
          {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}
        </Card>
        <Card icon={<Ic.Bulb />} title="Recomendações de gestão" sub="Ações sugeridas a partir dos achados">
          {reading
            ? <ol className={`gd-recs${updating ? ' is-stale' : ''}`}>{reading.recomendacoes.map((x, i) => <li key={i}><span className="mono">{i + 1}</span>{x}</li>)}</ol>
            : <p className="gd-empty">As recomendações chegam junto com a leitura.</p>}
        </Card>
      </div>
    </div>
  );
}

/* ── peças ────────────────────────────────────────────────────────── */

export function Card({ icon, title, sub, status, wide, className, children }: { icon: ReactNode; title: string; sub: string; status?: ReactNode; wide?: boolean; className?: string; children: ReactNode }) {
  return (
    <section className={`card gd-card${wide ? ' wide' : ''}${className ? ` ${className}` : ''}`}>
      <div className="gd-h">
        <h3>{icon}{title}</h3>
        {status}
      </div>
      <p className="sub">{sub}</p>
      {children}
    </section>
  );
}

function Kpi({ icon, title, sub, value, aside }: { icon: ReactNode; title: string; sub: string; value: number; aside: ReactNode }) {
  return (
    <section className="card gd-card gd-kpi">
      <div className="gd-h"><h3>{icon}{title}</h3></div>
      <p className="sub">{sub}</p>
      <div className="gd-kv"><b className="gd-big">{value}</b><span className="gd-aside">{aside}</span></div>
    </section>
  );
}

function RingKpi({ icon, title, sub, pct, detail }: { icon: ReactNode; title: string; sub: string; pct: number; detail: string }) {
  return (
    <section className="card gd-card gd-kpi">
      <div className="gd-h"><h3>{icon}{title}</h3></div>
      <p className="sub">{sub}</p>
      <div className="gd-kv">
        <Ring pct={pct} />
        <b className="gd-big">{pct}<small>%</small></b>
        <span className="gd-aside">{detail}</span>
      </div>
    </section>
  );
}

function Ring({ pct }: { pct: number }) {
  const r = 17, c = 2 * Math.PI * r;
  return (
    <svg className="gd-ring" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r={r} className="bg" />
      <circle cx="22" cy="22" r={r} className="fg" strokeDasharray={`${(Math.max(0, Math.min(100, pct)) / 100) * c} ${c}`} transform="rotate(-90 22 22)" />
    </svg>
  );
}

function Ranking({ total, unit, items, others, empty }: { total: number; unit: string; items: Ranked[]; others: Ranked | null; empty: string }) {
  if (!items.length) return <p className="gd-empty">{empty}</p>;
  const rows = others ? [...items, others] : items;
  const max = Math.max(...rows.map(x => x.count));
  return (
    <div className="gd-rank">
      <div className="gd-total"><b className="gd-big">{total}</b><span>{unit}</span></div>
      <ol>
        {rows.map((x, i) => (
          <li key={x.name + i} className={x === others ? 'other' : ''} title={`${x.name}: ${x.count} (${x.pct}%)`}>
            <span className="mono n">{x === others ? '·' : i + 1}</span>
            <span className="nm">{x.name}</span>
            <span className="tr"><i style={{ '--v': `${(x.count / max) * 100}%` } as CSSProperties} /></span>
            <span className="v"><b>{x.pct}%</b> ({x.count})</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function HBars({ items, max }: { items: Array<{ label: string; value: number; display: string; aside?: string; title?: string }>; max: number }) {
  return (
    <ul className="gd-bars">
      {items.map((x, i) => (
        <li key={x.label + i} title={x.title ?? `${x.label}: ${x.display}${x.aside ? ` · ${x.aside}` : ''}`}>
          <span className="nm">{x.label}</span>
          <span className="tr"><i style={{ '--v': `${Math.max(x.value > 0 ? 2 : 0, (x.value / max) * 100)}%` } as CSSProperties} /></span>
          <span className="v"><b>{x.display}</b>{x.aside && <em>{x.aside}</em>}</span>
        </li>
      ))}
    </ul>
  );
}

function Compare({ factors, updating }: { factors: Array<{ key: string; label: string; declared: number; real: number | null; why: string }>; updating: boolean }) {
  return (
    <div className="gd-cmp">
      <div className="gd-legend">
        <span><i className="dec" />Maturidade declarada</span>
        <span><i className="real" />Maturidade real (IA){updating && <em> · atualizando</em>}</span>
      </div>
      <div className="gd-cols" role="list">
        {factors.map(f => (
          <div className="gd-col" role="listitem" key={f.key} aria-label={`${f.label}: declarada ${f.declared}%, real ${f.real ?? '—'}%`}>
            <div className="gd-pair">
              <span className="gd-b dec" style={{ '--h': `${f.declared}%` } as CSSProperties} title={`Declarada: ${f.declared}%`}><b>{f.declared}%</b></span>
              <span className={`gd-b real${f.real == null ? ' none' : ''}${updating ? ' is-stale' : ''}`} style={{ '--h': `${f.real ?? 0}%` } as CSSProperties}
                title={f.real == null ? 'A leitura do agente ainda não chegou' : `Real (IA): ${f.real}% — ${f.why}`}><b>{f.real == null ? '—' : `${f.real}%`}</b></span>
            </div>
            <span className="lb">{f.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReadingStatus({ updating, basic, at }: { updating: boolean; basic: boolean; at: string | null }) {
  if (updating) return <span className="obj-st busy"><span className="spin" />Atualizando</span>;
  if (!at) return null;
  return <span className="obj-st" title={basic ? 'Sem IA: modo demonstração, ou o modelo não respondeu.' : undefined}>{basic ? 'Leitura básica' : 'Atualizada'} · {dateTime(at)}</span>;
}
